import React, { createContext, useReducer, useMemo, createRef, useContext } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { OrderedMap } from 'immutable';
import { TransitionGroup, Transition } from 'react-transition-group';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import { Notification } from '../../components/molecules/Notification';

const AnimatedNotification = styled(Notification)`
  position: relative;
  transition: transform 300ms linear, opacity 300ms linear;

  & + & {
    margin-top: 15px;
  }

  transform: translateY(
    ${({ state }) => {
      switch (state) {
        case 'exited':
          return '50%';
        case 'entering':
          return '0%';
        case 'entered':
          return '0%';
        case 'exiting':
          return '-50%';
        default:
          return '50%';
      }
    }}
  );
  opacity: ${({ state }) => {
    switch (state) {
      case 'exited':
        return 0;
      case 'entering':
        return 1;
      case 'entered':
        return 1;
      case 'exiting':
        return 0;
      default:
        return 0;
    }
  }};
`;

const StyledTransitionGroup = styled(TransitionGroup)`
  left: 50%;
  bottom: 50px;
  transform: translateX(-50%);
  position: absolute;
  z-index: var(--z-notification);

  display: flex;
  flex-direction: column;
`;

const initialState = { notifications: new OrderedMap() };

const reducer = (state, action) => {
  switch (action.type) {
    case 'ADD': {
      const id = uuid();
      const nodeRef = createRef(null);
      return {
        notifications: state.notifications.set(id, { ...action.payload, nodeRef })
      };
    }
    case 'REMOVE':
      return {
        notifications: state.notifications.delete(action.payload)
      };
    default:
      return state;
  }
};

const NotificationsContext = createContext();

const NotificationsProvider = ({ children = null, targetContainer = '#portal-root' }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const removeNotification = (id) => {
    dispatch({ type: 'REMOVE', payload: id });
  };

  const addNotification = (notification) => {
    dispatch({ type: 'ADD', payload: notification });
  };

  const contextValue = useMemo(() => ({ addNotification, removeNotification }), []);

  return (
    <>
      {ReactDOM.createPortal(
        <StyledTransitionGroup>
          {state.notifications.entrySeq().map(([id, notification]) => (
            <Transition key={id} nodeRef={notification.nodeRef} timeout={300} mountOnEnter>
              {(transitionState) => (
                <AnimatedNotification
                  ref={notification.nodeRef}
                  state={transitionState}
                  close={() => removeNotification(id)}
                  closeTimeout={5000}
                  undo={notification.undo}
                  icon={notification.icon}
                  highlight={notification.highlight}
                >
                  {notification.message}
                </AnimatedNotification>
              )}
            </Transition>
          ))}
        </StyledTransitionGroup>,
        document.querySelector(targetContainer)
      )}
      <NotificationsContext.Provider value={contextValue}>{children}</NotificationsContext.Provider>
    </>
  );
};

NotificationsProvider.propTypes = {
  targetContainer: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf([PropTypes.node]),
    PropTypes.string
  ])
};

const useNotifications = () => useContext(NotificationsContext);

export { NotificationsContext, NotificationsProvider, useNotifications };
